import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * HAR IDX-compliant disclaimer block.
 * Renders required brokerage attribution, data-reliability statement, source MLS,
 * and last-updated timestamp on every MLS-powered page.
 *
 * Props:
 *   listing (optional) — pass the listing object on detail pages so we render
 *     the "Listing courtesy of {office}" attribution in the same block.
 */
export default function IdxDisclaimer({ listing = null }) {
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    // Public endpoint — no auth needed. Silently ignore if unavailable.
    api.get("/mls/status")
      .then((r) => setLastUpdate(r.data?.last_run))
      .catch(() => setLastUpdate(null));
  }, []);

  const asOf = lastUpdate ? new Date(lastUpdate) : null;

  return (
    <section
      className="mt-14 border-t border-gray-200 pt-8 text-[11px] leading-relaxed text-gray-500 max-w-4xl mx-auto"
      data-testid="idx-disclaimer"
      aria-label="MLS IDX Disclaimer"
    >
      {listing?.list_office && (
        <p className="mb-3 text-[#1A1A1A]">
          <span className="uppercase tracking-[0.18em] text-[10px] text-gray-500 mr-2">
            Listing courtesy of
          </span>
          <span className="font-semibold">{listing.list_office}</span>
          {listing.list_agent ? ` · ${listing.list_agent}` : ""}
          {listing.listing_key ? ` · MLS #${listing.listing_key}` : ""}
        </p>
      )}

      <p className="mb-2">
        <strong className="text-[#1A1A1A]">Gens Realty, LLC</strong> is a licensed Texas real estate
        brokerage participating in the Internet Data Exchange (IDX) program of the Houston
        Association of REALTORS® (HAR) Multiple Listing Service.
      </p>

      <p className="mb-2">
        The information contained herein is furnished by the Houston Association of REALTORS® MLS
        via CoreLogic&nbsp;Trestle Web API. All information is provided exclusively for consumers&apos;
        personal, non-commercial use and may not be used for any purpose other than to identify
        prospective properties consumers may be interested in purchasing. Any use of search
        facilities of data on the site, other than by a consumer looking to purchase real estate,
        is prohibited.
      </p>

      <p className="mb-2">
        Information deemed reliable but <em>not</em> guaranteed. All property information should
        be independently verified. Prices, availability, and other listing details are subject to
        change without notice.
      </p>

      <p className="mb-2">
        Gens Realty, LLC is an <strong>Equal Housing Opportunity</strong> brokerage. Real estate
        services in Texas are provided under the Texas Real Estate Commission (TREC). See our{" "}
        <a href="/consumer-protection" className="underline hover:text-[#E63946]">
          Consumer Protection Notice
        </a>{" "}
        and{" "}
        <a href="/iabs" className="underline hover:text-[#E63946]">
          Information About Brokerage Services (IABS)
        </a>{" "}
        forms.
      </p>

      <p className="text-[10px] uppercase tracking-[0.18em] text-gray-400 mt-4">
        Source: Houston Association of REALTORS® · Delivered via CoreLogic Trestle
        {asOf && (
          <>
            {" · "}Data as of {asOf.toLocaleDateString()}{" "}
            {asOf.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </>
        )}
      </p>
    </section>
  );
}
